import React, {
    ReactElement, useCallback, useState,
} from "react";
import { AiOutlinePlus, AiOutlineUser } from "react-icons/ai";
import { BsBuilding, BsTrash } from "react-icons/bs";
import { GrOrganization } from "react-icons/gr";
import { Loading } from "..";
import { Link, Paths } from "../../contexts/Routing";
import { ErrorRemote } from "../../pages/error/Error";
import {
    GetOrganizationsDocument, useDeleteOrganizationMutation, useGetOrganizationsQuery,
} from "../../__generated__/api";
import Confirm from "../confirm/Confirm";
import ScrollableList from "../scrollable-list/ScrollableList";

export default function Organizations(): ReactElement {
    const [organizationSearch, setOrganizationSearch] = useState<string | undefined>(undefined);
    const [deleteOrganization] = useDeleteOrganizationMutation();
    const [organizationToDelete, setOrganizationToDelete] = useState<{ id: string; name: string; domain: string }>();

    const [isScrolling, setIsScrolling] = useState(false);

    const {
        error, loading: isLoading, data: organizationData, fetchMore,
    } = useGetOrganizationsQuery({
        variables: {
            query: organizationSearch,
        },
    });

    const handleScroll = useCallback(() => {
        setIsScrolling(true);
        if (organizationData?.companies.nextPageToken) {
            fetchMore({
                variables: {
                    nextPageToken: organizationData?.companies.nextPageToken,
                },
                updateQuery(previous, { fetchMoreResult }) {
                    return {
                        companies: {
                            id: fetchMoreResult.companies.nextPageToken,
                            count: previous.companies.count + fetchMoreResult.companies.count,
                            results: previous.companies.results.concat(
                                fetchMoreResult.companies.results,
                            ),
                            nextPageToken: fetchMoreResult.companies.nextPageToken,
                        },
                        __typename: fetchMoreResult.__typename,
                    };
                },
            });
        }
    }, [organizationData?.companies.nextPageToken]);

    if (isLoading && !isScrolling && !organizationSearch) {
        return <Loading />;
    }

    if (error) {
        return <ErrorRemote error={error} />;
    }
    const organizations = organizationData?.companies?.results ?? [];

    const handleDeleteOrganization = async (id: string) => {
        await deleteOrganization({
            refetchQueries: [
                { query: GetOrganizationsDocument },
            ],
            variables: {
                id,
            },
        });
        setOrganizationToDelete(undefined);
    };

    return (
        <div className="border p-3 rounded bg-white">
            <Confirm
                confirmText="Delete"
                onConfirm={() => {
                    if (!organizationToDelete) {
                        return;
                    }

                    handleDeleteOrganization(organizationToDelete.id);
                }}
                shouldShow={organizationToDelete != null}
                closeText="Close"
                onHide={() => setOrganizationToDelete(undefined)}
            >
                <div>
                    Are you sure you want to remove this organization?
                    {" "}
                    <div className="mt-2 p-2 rounded alert alert-danger">
                        {organizationToDelete?.name}
                        {" "}
                        (
                        {organizationToDelete?.domain}
                        )
                    </div>

                </div>
            </Confirm>

            {organizations.isNotEmpty || (organizationSearch !== "" && organizationSearch) ? (

                <div>
                    <div className="d-flex mb-3 align-self-center">
                        <div className="flex-grow-1">
                            <input
                                className="no-border w-100"
                                type="search"
                                placeholder="Search..."
                                value={organizationSearch}
                                onChange={evt => {
                                    setOrganizationSearch(evt.target.value);
                                    setIsScrolling(false);
                                }}
                            />
                        </div>
                        <Link
                            to={Paths.ORGANIZATIONS_NEW}
                            className="btn btn-none btn-sm mx-2"
                        >
                            <AiOutlinePlus />
                            {" "}
                            Organization
                        </Link>
                    </div>
                    <div className="list-group">
                        {organizations.isEmpty && organizationSearch ? (
                            <div>No results found</div>
                        ) : (
                            <ScrollableList
                                onScroll={handleScroll}
                            >
                                {organizations?.map(organization => (
                                    <div
                                        className="d-flex w-100 justify-content-between list-group-item align-items-center"
                                        key={organization.id}
                                    >
                                        <Link
                                            className="d-flex flex-grow-1 bg-none justify-content-start
                                            border-0 btn btn-none p-0 align-items-center"
                                            to={Paths.ORGANIZATIONS_ID}
                                            params={{ id: organization.id }}
                                        >
                                            <div>
                                                <BsBuilding className="text-light me-2" />
                                                {organization.name}
                                            </div>
                                            <small className="text-light ms-3">{organization.domain}</small>
                                        </Link>
                                        <div className="ms-2 text-light">
                                            <button
                                                className="btn btn-none text-gray-500 p-0"
                                                type="button"
                                                onClick={() => setOrganizationToDelete({
                                                    id: organization.id,
                                                    name: organization.name,
                                                    domain: organization.domain,
                                                })}
                                            >
                                                <BsTrash />
                                            </button>
                                        </div>
                                    </div>
                                ))}
                            </ScrollableList>
                        )}
                        <div className="d-flex justify-content-end align-items-center mt-3">

                            <Link
                                to={Paths.ORGANIZATIONS_NEW}
                                className="btn btn-secondary btn-sm"
                            >
                                <AiOutlinePlus />
                                {" "}
                                Organization
                            </Link>
                        </div>
                    </div>
                </div>
            ) : (
                <div className="d-flex flex-column mx-auto align-items-center col-4 text-center text-light my-4">
                    <h5 className="text-light">
                        Organizations
                    </h5>
                    <small>
                        Organizations are used to group users and give your agents visibility into
                        the entire customer. With Organizations,
                        you can assign notes to entire organizations and give your users visibility
                        into their teams tickets - not just their own.
                    </small>
                    <Link
                        to={Paths.ORGANIZATIONS_NEW}
                        className="btn btn-secondary btn-sm my-3"
                    >
                        <AiOutlinePlus />
                        {" "}
                        Add Organization
                    </Link>

                </div>
            )}
        </div>

    );
}
